import React, { useEffect } from 'react';

// Shared, print-safe attachment thumbnail for PDF / PDF-preview pages.
//
// Images render as <img> (unchanged behavior). Any non-image type (docx, xlsx, pdf,
// mp4, …) renders as a labeled file-type box instead of a broken <img>.
//
// `onReady` exists for the PDF pages that gate generation on every attachment having
// "loaded" (allImagesLoaded). It fires:
//   - for images: on load AND on error (so a failed/non-decodable image can't hang the
//     generation gate — the previous code only listened for onLoad);
//   - for file boxes: once on mount (there is nothing to load).

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'];

export const getAttachmentExtension = (nameOrKey = '') => {
  const clean = String(nameOrKey).toLowerCase().split('?')[0];
  const parts = clean.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export const isImageAttachment = (nameOrKey = '') =>
  IMAGE_EXTS.includes(getAttachmentExtension(nameOrKey));

const PdfAttachmentThumb = ({ url, name, index = 0, size = 200, onReady }) => {
  // Type detection prefers an explicit filename/key; falls back to the URL path
  // (signed URLs keep the key before the `?`, so the extension is still recoverable).
  const typeSource = name || url || '';
  const isImg = isImageAttachment(typeSource);

  useEffect(() => {
    if (!isImg && url && typeof onReady === 'function') onReady();
    // Fire once per mount; onReady is a readiness ping, not a render dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!url) return null;

  if (isImg) {
    return (
      <img
        src={url}
        alt={`Attachment ${index + 1}`}
        style={{ width: `${size}px`, height: `${size}px`, objectFit: 'cover' }}
        onLoad={onReady}
        onError={onReady}
      />
    );
  }

  const ext = (getAttachmentExtension(typeSource) || 'FILE').toUpperCase().slice(0, 4);
  const label =
    typeof name === 'string' && name ? name.split('?')[0].split('/').pop() : `Attachment ${index + 1}`;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '80px',
            borderRadius: '6px',
            backgroundColor: '#6c757d',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '15px',
            marginBottom: '10px',
          }}
        >
          {ext}
        </div>
        <div style={{ fontSize: '12px', color: '#495057', wordBreak: 'break-word', maxWidth: `${size - 20}px` }}>
          {label}
        </div>
      </div>
    </a>
  );
};

export default PdfAttachmentThumb;
